import { forwardRef } from "react";
import { cn } from "@/shared/lib/cn";
import { Avatar } from "./Avatar";
import { IconButton, type IconButtonProps } from "./IconButton";

export interface ProfileButtonProps extends Omit<IconButtonProps, "children"> {
  initial?: string;
  src?: string;
  seed?: string;
  balancePercent?: number | null;
  lowBalance?: boolean;
}

export type ProfileAvatarProps = Pick<ProfileButtonProps, "initial" | "src" | "seed" | "balancePercent" | "lowBalance">;

/** Decorative account identity; compose inside a button without nesting controls. */
export function ProfileAvatar({ initial, src, seed, balancePercent = null, lowBalance = false }: ProfileAvatarProps) {
  const circumference = 2 * Math.PI * 17;
  const remaining = typeof balancePercent === "number" && Number.isFinite(balancePercent)
    ? Math.max(0, Math.min(100, balancePercent))
    : null;
  return <span className="relative grid size-10 shrink-0 place-items-center" aria-hidden="true">
    <svg viewBox="0 0 40 40" fill="none" aria-hidden="true" style={{ width: 40, height: 40 }} className="pointer-events-none absolute inset-0 -rotate-90">
      <circle cx="20" cy="20" r="17" stroke="var(--line-strong)" strokeWidth="2" />
      {remaining !== null ? <circle cx="20" cy="20" r="17"
        stroke={lowBalance ? "var(--warning)" : "var(--ink-soft)"} strokeWidth="2.5"
        opacity={remaining > 0 ? 1 : 0} strokeLinecap="round" strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - remaining / 100)} /> : null}
    </svg>
    <Avatar label={initial} src={src} seed={seed} size={30} className="bg-[var(--secondary-control-surface)]" />
  </span>;
}

/** Account avatar and credit ring, shared by the topbar and component catalog. */
export const ProfileButton = forwardRef<HTMLButtonElement, ProfileButtonProps>(
  function ProfileButton(
    { initial, src, seed, balancePercent = null, lowBalance = false, className, ...props },
    ref,
  ) {
    return (
      <IconButton
        {...props}
        ref={ref}
        className={cn("relative size-11 sm:size-11", className)}
      >
        <ProfileAvatar initial={initial} src={src} seed={seed} balancePercent={balancePercent} lowBalance={lowBalance} />
      </IconButton>
    );
  },
);
